import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AdminLogin from "./AdminLogin";

const API_BASE = "https://api.mykcertifiedtranslations.com/api";

const STATUS_OPTIONS = ["New", "Replied"];


const QUOTE_STATUS_OPTIONS = [
  "new",
  "reviewing",
  "quoted",
  "waiting_payment",
  "paid",
  "in_progress",
  "completed",
  "delivered",
  "cancelled",
];

const ORDER_STATUS_OPTIONS = [
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "delivered", label: "Delivered" },
  { value: "cancelled", label: "Cancelled" },
];

const TABS = [
  { id: "quotes", label: "Quotes" },
  { id: "orders", label: "Orders" },
  { id: "messages", label: "Messages" },
  { id: "users", label: "Users" },
];

const getQuoteTrackingCode = (quote) => {
  if (quote?.tracking_code) {
    return quote.tracking_code.replace("#", "");
  }

  if (!quote?.id || !quote?.created_at) return "Not generated";

  const date = new Date(quote.created_at);
  if (Number.isNaN(date.getTime())) return "Not generated";

  const year = String(date.getFullYear()).slice(-2);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  const deadline = String(quote.deadline || "").toLowerCase();

  let urgencyCode = "S";

  if (deadline === "urgent") {
    urgencyCode = "U";
  }

  if (deadline === "express") {
    urgencyCode = "E";
  }

  return `${year}${month}${day}_${quote.id}_${urgencyCode}_UKT`;
};

const getFileExtension = (fileName = "") => {
  const cleanName = String(fileName);
  const lastDotIndex = cleanName.lastIndexOf(".");

  if (lastDotIndex === -1) return "";
  return cleanName.slice(lastDotIndex);
};

const getSafeTrackingFileName = (quote, file, index = 0) => {
  const trackingCode = getQuoteTrackingCode(quote)
    .replace("#", "")
    .replaceAll("/", "-")
    .replaceAll("\\", "-")
    .replaceAll(" ", "_");

  const originalName =
    file.originalName ||
    file.original_name ||
    file.filename ||
    file.storedName ||
    file.stored_name ||
    "";

  const extension = getFileExtension(originalName) || ".file";
  const fileRole = file.fileRole || file.file_role || "Source";

  if (fileRole === "Names") {
    const attachmentNumber = index > 0 ? index + 1 : "";
    return `${trackingCode}_A${attachmentNumber}${extension}`;
  }

  const fileNumber = index > 0 ? `_${index + 1}` : "";
  return `${trackingCode}${fileNumber}${extension}`;
};

export default function AdminInquiries() {
  const [adminToken, setAdminToken] = useState(
    localStorage.getItem("adminToken") || ""
  );
  const [ordersSearch, setOrdersSearch] = useState("");
  const [activeTab, setActiveTab] = useState("quotes");

  const [dashboard, setDashboard] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageStatusCounts, setMessageStatusCounts] = useState({
    New: 0,
    Replied: 0,
    Archived: 0,
    Deleted: 0,
    Exported: 0,
  });
  const [allMessages, setAllMessages] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);

  const [selectedMessage, setSelectedMessage] = useState(null);
  const [selectedQuote, setSelectedQuote] = useState(null);

  const [statusFilter, setStatusFilter] = useState("New");
  const [searchTerm, setSearchTerm] = useState("");
  const [quoteStatusFilter, setQuoteStatusFilter] = useState("");
  const [quoteSearchTerm, setQuoteSearchTerm] = useState("");
  const [orderStatusFilter, setOrderStatusFilter] = useState("in_progress");

  const [loading, setLoading] = useState(false);
  const [actionId, setActionId] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${adminToken}`,
    }),
    [adminToken]
  );



  const handleAuthFailure = useCallback(() => {
    localStorage.removeItem("adminToken");
    setAdminToken("");
    setErrorMessage("");
  }, []);

  const requestJson = useCallback(
    async (url, options = {}) => {
      const response = await fetch(url, {
        ...options,
        headers: {
          ...authHeaders,
          ...(options.headers || {}),
        },
      });

      const data = await response.json().catch(() => ({}));

      if (response.status === 401) {
        handleAuthFailure();
        throw new Error("Session expired. Please sign in again.");
      }

      if (!response.ok || data.success === false) {
        throw new Error(data.message || `Request failed with ${response.status}`);
      }

      return data;
    },
    [authHeaders, handleAuthFailure]
  );

  const loadDashboard = useCallback(async () => {
    const data = await requestJson(`${API_BASE}/admin/dashboard`);
    setDashboard(data);
  }, [requestJson]);

  const loadMessages = useCallback(async () => {
    const params = new URLSearchParams({
      page: "1",
      limit: "100",
      status: statusFilter,
      search: searchTerm.trim(),
      sort: "newest",
      dateRange: "all",
    });

    const data = await requestJson(`${API_BASE}/inquiries?${params.toString()}`);

    setMessages(Array.isArray(data.inquiries) ? data.inquiries : []);

    setMessageStatusCounts({
      New: Number(data.statusCounts?.New || 0),
      Replied: Number(data.statusCounts?.Replied || 0),
      Archived: Number(data.statusCounts?.Archived || 0),
      Deleted: Number(data.statusCounts?.Deleted || 0),
      Exported: Number(data.statusCounts?.Exported || 0),
    });
  }, [requestJson, searchTerm, statusFilter]);

  const loadQuotes = useCallback(async () => {
    const params = new URLSearchParams();

    if (quoteStatusFilter) params.set("status", quoteStatusFilter);
    if (quoteSearchTerm.trim()) params.set("search", quoteSearchTerm.trim());

    const data = await requestJson(`${API_BASE}/admin/quotes?${params.toString()}`);
    setQuotes(Array.isArray(data.quotes) ? data.quotes : []);
  }, [requestJson, quoteSearchTerm, quoteStatusFilter]);

  const loadUsers = useCallback(async () => {
    const data = await requestJson(`${API_BASE}/admin/users`);
    setUsers(Array.isArray(data.users) ? data.users : []);
  }, [requestJson]);

  const loadOrders = useCallback(async () => {
    const data = await requestJson(`${API_BASE}/orders`);
    setOrders(Array.isArray(data.orders) ? data.orders : []);
  }, [requestJson]);

  const refresh = useCallback(async () => {
    if (!adminToken) return;

    setLoading(true);
    setErrorMessage("");

    try {
      await loadDashboard();

      if (activeTab === "messages") await loadMessages();
      if (activeTab === "quotes") await loadQuotes();
      if (activeTab === "users") await loadUsers();
      if (activeTab === "orders") await loadOrders();
    } catch (error) {
      setErrorMessage(error.message || "Failed to load admin dashboard.");
    } finally {
      setLoading(false);
    }
  }, [
    activeTab,
    adminToken,
    loadDashboard,
    loadMessages,
    loadOrders,
    loadQuotes,
    loadUsers,
  ]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleLoginSuccess = (token) => {
    localStorage.setItem("adminToken", token);
    setAdminToken(token);
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    setAdminToken("");
    setDashboard(null);
    setMessages([]);
    setQuotes([]);
    setUsers([]);
    setOrders([]);
    setSelectedMessage(null);
    setSelectedQuote(null);
  };

  const updateStatus = async (messageId, status) => {
    setActionId(`status-${messageId}`);

    try {
      await requestJson(`${API_BASE}/inquiries/${messageId}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });

      await loadDashboard();
      await loadMessages();

      setSelectedMessage((current) =>
        current?.id === messageId ? { ...current, status } : current
      );
    } catch (error) {
      alert(error.message || "Failed to update status.");
    } finally {
      setActionId("");
    }
  };

  const updateQuoteStatus = async (quoteId, status) => {
    setActionId(`quote-status-${quoteId}`);

    try {
      await requestJson(`${API_BASE}/admin/quotes/${quoteId}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });

      if (status === "in_progress") {
        setQuotes((currentQuotes) =>
          currentQuotes.filter((quote) => quote.id !== quoteId)
        );

        setSelectedQuote(null);
      } else {
        setQuotes((currentQuotes) =>
          currentQuotes.map((quote) =>
            quote.id === quoteId ? { ...quote, status } : quote
          )
        );

        setSelectedQuote((currentQuote) =>
          currentQuote?.id === quoteId ? { ...currentQuote, status } : currentQuote
        );
      }

      await loadQuotes();
    } catch (error) {
      alert(error.message || "Failed to update quote status.");
    } finally {
      setActionId("");
    }
  };

  const updateQuotePrice = async (quoteId, estimated_price) => {
    setActionId(`quote-price-${quoteId}`);

    try {
      await requestJson(`${API_BASE}/admin/quotes/${quoteId}/price`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ estimated_price }),
      });

      setQuotes((currentQuotes) =>
        currentQuotes.map((quote) =>
          quote.id === quoteId ? { ...quote, estimated_price } : quote
        )
      );

      setSelectedQuote((currentQuote) =>
        currentQuote?.id === quoteId ? { ...currentQuote, estimated_price } : currentQuote
      );

      await loadQuotes();
    } catch (error) {
      alert(error.message || "Failed to update quote price.");
    } finally {
      setActionId("");
    }
  };

  const [selectedOrder, setSelectedOrder] = useState(null);

  const deleteQuote = async (quoteId) => {
    const confirmed = window.confirm("Delete this quote request?");
    if (!confirmed) return;

    setActionId(`quote-delete-${quoteId}`);

    try {
      await requestJson(`${API_BASE}/admin/quotes/${quoteId}`, {
        method: "DELETE",
      });

      setSelectedQuote(null);

      setQuotes((currentQuotes) =>
        currentQuotes.filter((quote) => quote.id !== quoteId)
      );

      await loadDashboard();
      await loadQuotes();
    } catch (error) {
      alert(error.message || "Failed to delete quote.");
    } finally {
      setActionId("");
    }
  };

const sendQuotePaymentLink = async (quoteId) => {
  const confirmed = window.confirm(
    "Send this quote with a Stripe payment link to the client?"
  );

  if (!confirmed) return;

  setActionId(`quote-payment-${quoteId}`);

  try {
    const data = await requestJson(
      `${API_BASE}/admin/quotes/${quoteId}/send-payment-link`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      }
    );

    setQuotes((currentQuotes) =>
      currentQuotes.map((quote) =>
        quote.id === quoteId ? { ...quote, status: "waiting_payment" } : quote
      )
    );

    setSelectedQuote((currentQuote) =>
      currentQuote?.id === quoteId
        ? { ...currentQuote, status: "waiting_payment" }
        : currentQuote
    );

    await loadDashboard();
    await loadQuotes();

    alert(data.message || "Quote email with payment link sent successfully.");
  } catch (error) {
    alert(error.message || "Failed to send payment link.");
  } finally {
    setActionId("");
  }
};

  const convertQuoteToOrder = async (quoteId) => {
    const confirmed = window.confirm("Convert this quote into an order?");
    if (!confirmed) return;

    setActionId(`quote-convert-${quoteId}`);

    try {
      const data = await requestJson(`${API_BASE}/orders/from-quote/${quoteId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      setSelectedQuote(null);

      setQuotes((currentQuotes) =>
        currentQuotes.filter((quote) => quote.id !== quoteId)
      );

      await loadDashboard();
      await loadQuotes();
      await loadOrders();

      alert(data.message || "Quote converted to order successfully.");
    } catch (error) {
      alert(error.message || "Failed to convert quote to order.");
    } finally {
      setActionId("");
    }
  };

  const deleteOrder = async (orderId) => {
    const confirmed = window.confirm("Delete this order?");
    if (!confirmed) return;

    setActionId(`order-delete-${orderId}`);

    try {
      await requestJson(`${API_BASE}/orders/${orderId}`, {
        method: "DELETE",
      });

      setOrders((currentOrders) =>
        currentOrders.filter((order) => order.id !== orderId)
      );

      await loadDashboard();
      await loadOrders();

      alert("Order deleted successfully.");
    } catch (error) {
      alert(error.message || "Failed to delete order.");
    } finally {
      setActionId("");
    }
  };

const saveOrderInternalNote = async (orderId, internalNote) => {
  if (!orderId) return;

  setActionId(`order-internal-note-${orderId}`);

  try {
    const data = await requestJson(`${API_BASE}/orders/${orderId}/internal-note`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ internalNote }),
    });

    setOrders((currentOrders) =>
      currentOrders.map((order) =>
        order.id === orderId
          ? {
              ...order,
              ...(data.order || {}),
            }
          : order
      )
    );

    if (data.order) {
      setSelectedOrder(data.order);
    }

    await loadOrders();

    alert(data.message || "Internal note saved successfully.");
  } catch (error) {
    alert(error.message || "Failed to save internal note.");
  } finally {
    setActionId("");
  }
};  
const restoreDeletedOrder = async (orderId) => {
  const confirmed = window.confirm("Restore this order from recycle bin?");
  if (!confirmed) return;

  setActionId(`order-restore-deleted-${orderId}`);

  try {
    await requestJson(`${API_BASE}/orders/${orderId}/restore`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
    });

    await loadDashboard();
    await loadOrders();

    alert("Order restored successfully.");
  } catch (error) {
    alert(error.message || "Failed to restore order.");
  } finally {
    setActionId("");
  }
};

  const permanentlyDeleteOrder = async (orderId) => {
    const confirmed = window.confirm(
      "Permanently delete this order? This cannot be undone."
    );

    if (!confirmed) return;

    setActionId(`order-permanent-delete-${orderId}`);

    try {
      await requestJson(`${API_BASE}/orders/${orderId}/permanent`, {
        method: "DELETE",
      });

      await loadDashboard();
      await loadOrders();

      alert("Order permanently deleted.");
    } catch (error) {
      alert(error.message || "Failed to permanently delete order.");
    } finally {
      setActionId("");
    }
  };
  const updateOrderStatus = async (orderId, status) => {
    setActionId(`order-status-${orderId}`);

    try {
      const data = await requestJson(`${API_BASE}/orders/${orderId}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });

      setOrders((currentOrders) =>
        currentOrders.map((order) =>
          order.id === orderId
            ? {
                ...order,
                ...(data.order || {}),
                status: data.order?.status || status,
              }
            : order
        )
      );

      await loadDashboard();
      await loadOrders();
    } catch (error) {
      alert(error.message || "Failed to update order status.");
    } finally {
      setActionId("");
    }
  };
  const exportMessage = (message) => {
    const clientName =
      message.fullName ||
      message.full_name ||
      message.client_name ||
      "Unknown client";

    const clientEmail =
      message.emailAddress ||
      message.email_address ||
      message.email ||
      "No email";

    const subject =
      message.subject ||
      message.documentType ||
      message.document_type ||
      "No subject";

    const body =
      message.message ||
      message.notes ||
      "No message provided.";

    const submitted =
      formatDate(message.createdAt || message.created_at);

    const content = `
MESSAGE EXPORT
====================

Client: ${clientName}
Email: ${clientEmail}
Subject: ${subject}
Status: ${message.status || "New"}
Submitted: ${submitted}

Message:
${body}
`;

    const blob = new Blob([content], {
      type: "text/plain;charset=utf-8",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `message-${message.id || "export"}.txt`;
    document.body.appendChild(link);
    link.click();
    link.remove();

    URL.revokeObjectURL(url);
  };
  const deleteMessage = async (messageId) => {
    const confirmed = window.confirm("Delete this message and its uploaded files?");
    if (!confirmed) return;

    setActionId(`delete-${messageId}`);

    try {
      await requestJson(`${API_BASE}/inquiries/${messageId}`, {
        method: "DELETE",
      });

      setSelectedMessage(null);
      await loadDashboard();
      await loadMessages();
    } catch (error) {
      alert(error.message || "Failed to delete message.");
    } finally {
      setActionId("");
    }
  };
  const archiveMessage = async (messageId) => {
    setActionId(`message-archive-${messageId}`);

    try {
      await requestJson(`${API_BASE}/inquiries/${messageId}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "Archived" }),
      });

      await loadDashboard();
      await loadMessages();
    } catch (error) {
      alert(error.message || "Failed to archive message.");
    } finally {
      setActionId("");
    }
  };

  const restoreMessage = async (messageId) => {
    setActionId(`message-restore-${messageId}`);

    try {
      await requestJson(`${API_BASE}/inquiries/${messageId}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "New" }),
      });

      await loadDashboard();
      await loadMessages();
    } catch (error) {
      alert(error.message || "Failed to restore message.");
    } finally {
      setActionId("");
    }
  };

  const permanentlyDeleteMessage = async (messageId) => {
    const confirmed = window.confirm(
      "Permanently delete this message? This cannot be undone."
    );

    if (!confirmed) return;

    setActionId(`message-permanent-delete-${messageId}`);

    try {
      await requestJson(`${API_BASE}/inquiries/${messageId}/permanent`, {
        method: "DELETE",
      });

      await loadDashboard();
      await loadMessages();
    } catch (error) {
      alert(error.message || "Failed to permanently delete message.");
    } finally {
      setActionId("");
    }
  };
  const permanentlyDeleteMessages = async (messageIds) => {
    const validMessageIds = [...new Set(messageIds || [])].filter(Boolean);

    if (!validMessageIds.length) {
      alert("There are no exported messages to delete.");
      return;
    }

    const confirmed = window.confirm(
      `Permanently delete ${validMessageIds.length} exported message(s)? This cannot be undone.`
    );

    if (!confirmed) return;

    setActionId("messages-bulk-permanent-delete");

    try {
      await Promise.all(
        validMessageIds.map((messageId) =>
          requestJson(`${API_BASE}/inquiries/${messageId}/permanent`, {
            method: "DELETE",
          })
        )
      );

      await loadDashboard();
      await loadMessages();

      alert(`${validMessageIds.length} message(s) permanently deleted.`);
    } catch (error) {
      alert(
        error.message ||
        "Failed to permanently delete exported messages."
      );
    } finally {
      setActionId("");
    }
  };  
  const markMessagesAsExported = async (messageIds) => {
    const validMessageIds = [...new Set(messageIds || [])].filter(Boolean);

    if (!validMessageIds.length) return;

    setActionId("messages-mark-exported");

    try {
      await Promise.all(
        validMessageIds.map((messageId) =>
          requestJson(`${API_BASE}/inquiries/${messageId}/status`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              status: "Exported",
            }),
          })
        )
      );

      await loadDashboard();

      setStatusFilter("Exported");
    } catch (error) {
      alert(
        error.message ||
        "The file was exported, but the messages could not be moved."
      );
    } finally {
      setActionId("");
    }
  };
  const sendMessageReply = async (messageId, replyMessage) => {
    setActionId(`reply-${messageId}`);

    try {
      const data = await requestJson(`${API_BASE}/inquiries/${messageId}/reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ replyMessage }),
      });

      const loadedMessages = data.messages || [];

      setAllMessages(loadedMessages);
      setMessages(loadedMessages);

      setSelectedMessage((currentMessage) =>
        currentMessage?.id === messageId
          ? {
            ...currentMessage,
            ...(data.inquiry || {}),
            status: data.inquiry?.status || "Replied",
          }
          : currentMessage
      );

      await loadDashboard();
      await loadMessages();

      alert(data.message || "Reply sent successfully.");
    } catch (error) {
      alert(error.message || "Failed to send reply.");
    } finally {
      setActionId("");
    }
  };
  const downloadMessageFile = async (messageId, fileIndex, originalName) => {
    try {
      const response = await fetch(
        `${API_BASE}/inquiries/${messageId}/files/${fileIndex}/download`,
        { headers: authHeaders }
      );

      if (response.status === 401) {
        handleAuthFailure();
        return;
      }

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || "Failed to download file.");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = originalName || "uploaded-file";

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert(error.message || "Failed to download file.");
    }
  };

  const uploadFinalTranslation = async (orderId, files) => {
    const selectedFiles = Array.from(files || []);

    if (!selectedFiles.length) return;

    const confirmed = window.confirm(
      "Upload this final translation and move the order to Ready to Send?"
    );

    if (!confirmed) return;

    setActionId(`order-upload-final-${orderId}`);

    try {
      const formData = new FormData();

      selectedFiles.forEach((file) => {
        formData.append("finalFiles", file);
      });

      const response = await fetch(`${API_BASE}/orders/${orderId}/final-files`, {
        method: "POST",
        headers: authHeaders,
        body: formData,
      });

      const data = await response.json().catch(() => ({}));

      if (response.status === 401) {
        handleAuthFailure();
        return;
      }

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Failed to upload final translation.");
      }

      await loadDashboard();
      await loadOrders();

      alert(data.message || "Final translation uploaded successfully.");
    } catch (error) {
      alert(error.message || "Failed to upload final translation.");
    } finally {
      setActionId("");
    }
  };

  const sendFinalTranslationEmail = async (orderId) => {
    const confirmed = window.confirm(
      "Send the final translation to the client by email?"
    );

    if (!confirmed) return;

    setActionId(`order-send-final-${orderId}`);

    try {
      const data = await requestJson(
        `${API_BASE}/orders/${orderId}/send-final-email`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        }
      );

      await loadDashboard();
      await loadOrders();

      alert(data.message || "Final translation sent successfully.");
    } catch (error) {
      alert(error.message || "Failed to send final translation.");
    } finally {
      setActionId("");
    }
  };

  const downloadOrderZip = async (order) => {
    try {
      const orderId = order.id;

      const orderNumber =
        order.orderNumber ||
        order.order_number ||
        order.trackingCode ||
        order.tracking_code ||
        `ORDER-${order.id}`;

      const response = await fetch(
        `${API_BASE}/orders/${orderId}/files/download-zip`,
        { headers: authHeaders }
      );

      if (response.status === 401) {
        handleAuthFailure();
        return;
      }

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || "Failed to download ZIP file.");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `${orderNumber}.zip`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert(error.message || "Failed to download ZIP file.");
    }
  };

  const downloadUploadedFile = async (fileId, originalName) => {
    try {
      const response = await fetch(
        `${API_BASE}/admin/uploaded-files/${fileId}/download`,
        { headers: authHeaders }
      );

      if (response.status === 401) {
        handleAuthFailure();
        return;
      }

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || "Failed to download file.");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = originalName || "uploaded-file";

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert(error.message || "Failed to download file.");
    }
  };

  const downloadQuoteFile = async (quote, file, index = 0) => {
    try {
      if (!file || !file.id) {
        throw new Error("File ID is missing.");
      }

      const response = await fetch(
        `${API_BASE}/admin/uploaded-files/${file.id}/download`,
        { headers: authHeaders }
      );

      if (response.status === 401) {
        handleAuthFailure();
        return;
      }

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || "Failed to download file.");
      }

      const downloadName = getSafeTrackingFileName(quote, file, index);

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = downloadName;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert(error.message || "Failed to download file.");
    }
  };

  const formatDate = (value) => {
    if (!value) return "Not set";

    return new Date(value).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatMoney = (amount, currency = "GBP") => {
    if (amount === null || amount === undefined || amount === "") return "Not priced";

    return `${Number(amount).toLocaleString("en-GB", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${currency}`;
  };

  if (!adminToken) {
    return <AdminLogin onLoginSuccess={handleLoginSuccess} />;
  }

  const metrics = dashboard?.metrics || {
    contactMessages: 0,
    quotes: 0,
    quoteRequests: 0,
    users: 0,
    orders: 0,
  };

  return (
    <main className="admin-dashboard-shell">
      <aside className="admin-sidebar">
        <div>
          <Link to="/" className="admin-site-link">
            Back to Website
          </Link>

          <div className="admin-brand-block">
            <span className="section-label">Admin</span>
            <h1>Dashboard</h1>
            <p>Manage enquiries, quotes, clients, and translation orders.</p>
          </div>
        </div>

        <nav className="admin-tab-nav" aria-label="Admin sections">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={activeTab === tab.id ? "active-admin-tab" : ""}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        <button type="button" className="admin-logout-inline" onClick={handleLogout}>
          Logout
        </button>
      </aside>

      <section className="admin-dashboard-main">
        <div className="admin-dashboard-header">
          <div>
            <p className="admin-eyebrow">Secure internal workspace</p>
            <h2>{TABS.find((tab) => tab.id === activeTab)?.label}</h2>
          </div>

          <button type="button" className="admin-refresh-btn" onClick={refresh}>
            Refresh
          </button>
        </div>

        <div className="admin-metric-grid">
          <Metric
            label="Quotes"
            value={metrics.quotes || metrics.quoteRequests || quotes.length || 0}
          />
          <Metric label="Orders" value={metrics.orders} />
          <Metric label="Messages" value={metrics.contactMessages} />
          <Metric label="Users" value={metrics.users} />
        </div>

        {errorMessage && <p className="admin-alert">{errorMessage}</p>}
        {loading && <p className="admin-message">Loading admin data...</p>}

        {activeTab === "messages" && (
          <MessagesTab
            messages={messages}
            statusCounts={messageStatusCounts}
            statusFilter={statusFilter}
            searchTerm={searchTerm}
            actionId={actionId}
            onSearchChange={setSearchTerm}
            onStatusFilterChange={setStatusFilter}
            onSelectMessage={setSelectedMessage}
            onStatusChange={updateStatus}
            onArchive={archiveMessage}
            onRestore={restoreMessage}
            onPermanentDelete={permanentlyDeleteMessage}
            onBulkDelete={permanentlyDeleteMessages}
            onMarkExported={markMessagesAsExported}
            onDelete={deleteMessage}
            formatDate={formatDate}
          />
        )}

        {!loading && activeTab === "quotes" && (
          <QuotesTab
            quotes={quotes}
            statusFilter={quoteStatusFilter}
            searchTerm={quoteSearchTerm}
            actionId={actionId}
            onSearchChange={setQuoteSearchTerm}
            onStatusFilterChange={setQuoteStatusFilter}
            onStatusChange={updateQuoteStatus}
            onPriceChange={updateQuotePrice}
            onDeleteQuote={deleteQuote}
            onConvertQuote={convertQuoteToOrder}
            onSelectQuote={setSelectedQuote}
            formatDate={formatDate}
            formatMoney={formatMoney}
          />
        )}

        {!loading && activeTab === "users" && (
          <UsersTab users={users} formatDate={formatDate} />
        )}

        {!loading && activeTab === "orders" && (
          <OrdersTab
            orders={orders}
            orderStatusFilter={orderStatusFilter}
            onOrderStatusFilterChange={setOrderStatusFilter}
            ordersSearch={ordersSearch}
            onOrdersSearchChange={setOrdersSearch}
            formatDate={formatDate}
            formatMoney={formatMoney}
            onDownloadZip={downloadOrderZip}
            onDownloadFinalFile={downloadUploadedFile}
            onDeleteOrder={deleteOrder}
            onRestoreDeletedOrder={restoreDeletedOrder}
            onPermanentDeleteOrder={permanentlyDeleteOrder}
            onStatusChange={updateOrderStatus}
            onUploadFinalTranslation={uploadFinalTranslation}
            onSendFinalTranslationEmail={sendFinalTranslationEmail}
            onViewQuoteInfo={setSelectedOrder}
            actionId={actionId}
          />
        )}

        {selectedMessage && (
          <MessageModal
            message={selectedMessage}
            actionId={actionId}
            onClose={() => setSelectedMessage(null)}
            onStatusChange={updateStatus}
            onDelete={deleteMessage}
            onDownload={downloadMessageFile}
            onReply={sendMessageReply}
            formatDate={formatDate}
          />
        )}

        {selectedQuote && (
          <QuoteModal
            quote={selectedQuote}
            actionId={actionId}
            onClose={() => setSelectedQuote(null)}
            onStatusChange={updateQuoteStatus}
            onPriceChange={updateQuotePrice}
            onDeleteQuote={deleteQuote}
            onConvertQuote={convertQuoteToOrder}
            onDownloadQuoteFile={downloadQuoteFile}
            onSendPaymentLink={sendQuotePaymentLink}
            formatDate={formatDate}
            formatMoney={formatMoney}
          />
        )}
        {selectedOrder && (
          <OrderQuoteInfoModal
            order={selectedOrder}
            onClose={() => setSelectedOrder(null)}
            formatDate={formatDate}
            formatMoney={formatMoney}
            actionId={actionId}
            onSaveInternalNote={saveOrderInternalNote}
          />
        )}
      </section>
    </main>
  );
}

function OrderQuoteInfoModal({
  order,
  onClose,
  formatDate,
  formatMoney,
  actionId,
  onSaveInternalNote,
}) {
  const orderEvents = Array.isArray(order.events) ? order.events : [];

  const orderNumber =
    order.orderNumber ||
    order.order_number ||
    order.trackingCode ||
    order.tracking_code ||
    `ORDER-${order.id}`;

  const clientName =
    order.userFullName ||
    order.user_full_name ||
    order.client_name ||
    order.full_name ||
    "Unassigned";

  const clientEmail =
    order.userEmail ||
    order.user_email ||
    order.client_email ||
    order.email ||
    "No email";

  const sourceLanguage =
    order.sourceLanguage || order.source_language || "Not specified";

  const targetLanguage =
    order.targetLanguage || order.target_language || "Not specified";

  const translationType =
    order.translationType || order.translation_type || "Not specified";

  const deadline = order.deadline || "Not specified";
  const status = order.status || "in_progress";
  const message = order.message || order.notes || "No message provided.";

  const files = Array.isArray(order.files) ? order.files : [];

const getFileRole = (file) =>
  String(file.fileRole || file.file_role || "Source")
    .toLowerCase()
    .replaceAll(" ", "_");

const sourceFiles = files.filter((file) => getFileRole(file) === "source");
const namesFiles = files.filter((file) => getFileRole(file) === "names");
const finalFiles = files.filter((file) => getFileRole(file) === "final");

const otherFiles = files.filter((file) => {
  const role = getFileRole(file);
  return !["source", "names", "final"].includes(role);
});

const getFileName = (file) =>
  file.originalName ||
  file.original_name ||
  file.filename ||
  file.stored_name ||
  "Uploaded file";

const renderFileGroup = (title, fileList) => (
  <div className="order-file-group">
    <div className="order-file-group-header">
      <span>{title}</span>
      <strong>{fileList.length}</strong>
    </div>

    {fileList.length > 0 ? (
      <div className="admin-files-list">
        {fileList.map((file, index) => (
          <div
            key={file.id || `${title}-${index}`}
            className="admin-file-row order-file-row-with-role"
          >
            <span title={getFileName(file)}>{getFileName(file)}</span>
          </div>
        ))}
      </div>
    ) : (
      <p className="order-file-empty">No files in this section.</p>
    )}
  </div>
);

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div
        className="admin-modal order-quote-info-modal"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="admin-modal-header">
          <div>
            <h3>{orderNumber}</h3>
            <p>Quote / Order information</p>
          </div>

          <button type="button" className="admin-modal-close" onClick={onClose}>
            X
          </button>
        </div>

        <div className="admin-modal-body">
          <div className="admin-detail-grid">
            <div>
              <span className="admin-label">Tracking Code</span>
              <p>{orderNumber}</p>
            </div>

            <div>
              <span className="admin-label">Client Name</span>
              <p>{clientName}</p>
            </div>

            <div>
              <span className="admin-label">Client Email</span>
              <p>{clientEmail}</p>
            </div>

            <div>
              <span className="admin-label">Amount</span>
              <p>
                {formatMoney(
                  order.amount ?? order.total_amount ?? order.price ?? order.quote_estimated_price ?? 0,
                  order.currency || "GBP"
                )}
              </p>
            </div>

            <div>
              <span className="admin-label">Created</span>
              <p>{formatDate(order.createdAt || order.created_at)}</p>
            </div>

            <div>
              <span className="admin-label">Status</span>
              <p>{String(status).replaceAll("_", " ")}</p>
            </div>

            <div>
              <span className="admin-label">Source Language</span>
              <p>{sourceLanguage}</p>
            </div>

            <div>
              <span className="admin-label">Target Language</span>
              <p>{targetLanguage}</p>
            </div>

            <div>
              <span className="admin-label">Translation Type</span>
              <p>{translationType}</p>
            </div>

            <div>
              <span className="admin-label">Deadline</span>
              <p>{deadline}</p>
            </div>
          </div>

          <div className="admin-modal-row">
            <span className="admin-label">Client Message / Notes</span>
            <p className="admin-message-text">{message}</p>
          </div>

          <div className="admin-modal-row">
            <span className="admin-label">Files</span>

            <div className="order-file-groups">
              {renderFileGroup("Source documents", sourceFiles)}
              {renderFileGroup("Names list / spelling files", namesFiles)}
              {renderFileGroup("Final translation files", finalFiles)}

              {otherFiles.length > 0 &&
                renderFileGroup("Other files", otherFiles)}
            </div>
          </div>

          <div className="order-internal-note-section">
            <span className="admin-label">Internal Note</span>

            <textarea
              id={`order-internal-note-${order.id}`}
              className="admin-control-textarea"
              defaultValue={order.internalNote || order.internal_note || ""}
              rows="4"
              placeholder="Write private admin notes here..."
            />

            <button
              type="button"
              className="admin-primary-btn"
              disabled={actionId === `order-internal-note-${order.id}`}
              onClick={() => {
                const note =
                  document.getElementById(`order-internal-note-${order.id}`)
                    ?.value || "";

                onSaveInternalNote(order.id, note);
              }}
            >
              {actionId === `order-internal-note-${order.id}`
                ? "Saving..."
                : "Save Internal Note"}
            </button>
          </div>

          <div className="order-timeline-section">
            <span className="admin-label">Activity Timeline</span>

            {orderEvents.length === 0 ? (
              <p className="admin-empty-panel">No activity recorded yet.</p>
            ) : (
              <div className="order-timeline-list">
                {orderEvents.map((event) => (
                  <div key={event.id} className="order-timeline-item">
                    <strong>
                      {event.title || event.eventType || event.event_type}
                    </strong>

                    {event.description && <p>{event.description}</p>}

                    <small>
                      {formatDate(event.createdAt || event.created_at)}
                    </small>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="admin-metric-card">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function QuotesTab({
  quotes,
  statusFilter,
  searchTerm,
  actionId,
  onSearchChange,
  onStatusFilterChange,
  onStatusChange,
  onPriceChange,
  onDeleteQuote,
  onConvertQuote,
  onSelectQuote,
  formatDate,
  formatMoney,
}) {
  const [priceDrafts, setPriceDrafts] = useState({});

  return (
    <div className="admin-panel">
      <div className="admin-panel-toolbar">
        <input
          type="search"
          value={searchTerm}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search quotes"
          className="admin-control-input"
        />

        <select
          value={statusFilter}
          onChange={(event) => onStatusFilterChange(event.target.value)}
          className="admin-control-select"
        >
          <option value="">All active quotes</option>
          <option value="new">New</option>
          <option value="reviewing">Reviewing</option>
          <option value="quoted">Quoted</option>
          <option value="waiting_payment">Waiting payment</option>
          <option value="paid">Paid</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {quotes.length === 0 ? (
        <p className="admin-empty-panel">No quotes found.</p>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-data-table">
            <thead>
              <tr>
                <th>Tracking Code</th>
                <th>Client</th>
                <th>Languages</th>
                <th>Status</th>
                <th>Price</th>
                <th>Submitted</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {quotes.map((quote) => {
                const priceDraft = priceDrafts[quote.id] ?? quote.estimated_price ?? "";

                return (
                  <tr key={quote.id}>
                    <td>
                      <strong>{getQuoteTrackingCode(quote)}</strong>
                    </td>

                    <td>
                      <h3>{quote.full_name || quote.fullName || quote.name || "Unknown client"}</h3>
                      <p>{quote.email || quote.email_address || quote.emailAddress || "No email"}</p>
                    </td>

                    <td>
                      {(quote.source_language || "Source")} â†’ {quote.target_language || "Target"}
                    </td>

                    <td className="admin-status-cell">
                      <div className="admin-status-cell-inner">
                        <span
                          className={`quote-status-badge quote-status-${String(
                            quote.status || "new"
                          )
                            .toLowerCase()
                            .replaceAll(" ", "_")}`}
                        >
                          {String(quote.status || "new").replaceAll("_", " ")}
                        </span>
                      </div>
                    </td>

                    <td>
                      <div className="admin-row-actions-compact">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={priceDraft}
                          onChange={(event) =>
                            setPriceDrafts((current) => ({
                              ...current,
                              [quote.id]: event.target.value,
                            }))
                          }
                          className="admin-control-input"
                          style={{ maxWidth: "110px" }}
                        />

                        <button
                          type="button"
                          onClick={() => onPriceChange(quote.id, priceDraft)}
                          disabled={actionId === `quote-price-${quote.id}`}
                        >
                          Save
                        </button>
                      </div>

                      <small>{formatMoney(quote.estimated_price, "GBP")}</small>
                    </td>

                    <td>{formatDate(quote.created_at)}</td>

                    <td>
                      <div className="admin-row-actions-compact">
                        <button type="button" onClick={() => onSelectQuote(quote)}>
                          View
                        </button>

                        <button
                          type="button"
                          onClick={() => onConvertQuote(quote.id)}
                          disabled={actionId === `quote-convert-${quote.id}`}
                        >
                          Convert to Order
                        </button>

                        <button
                          type="button"
                          className="danger-action"
                          onClick={() => onDeleteQuote(quote.id)}
                          disabled={actionId === `quote-delete-${quote.id}`}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MessagesTab({
  messages,
  statusCounts,
  statusFilter,
  searchTerm,
  actionId,
  onSearchChange,
  onStatusFilterChange,
  onSelectMessage,
  onArchive,
  onRestore,
  onPermanentDelete,
  onBulkDelete,
  onDelete,
  formatDate,
  onMarkExported,
}) {
  const messageTabs = [
    { value: "New", label: "New messages" },
    { value: "Replied", label: "Replied" },
    { value: "Archived", label: "Archived" },
    { value: "Deleted", label: "Deleted" },
    { value: "Exported", label: "Exported" },
  ];
  const normalizeMessageStatus = (status) => {
    const value = String(status || "New").toLowerCase().trim();

    if (value === "replied") return "Replied";
    if (value === "archived") return "Archived";
    if (value === "deleted") return "Deleted";
    if (value === "exported") return "Exported";

    return "New";
  };

  const sourceMessages = Array.isArray(messages) ? messages : [];

  const getMessageStatus = (message) =>
    normalizeMessageStatus(message.status);

  const getMessageCount = (status) => Number(statusCounts?.[status] || 0);

  const filteredMessages = sourceMessages.filter((message) => {
    const messageStatus = getMessageStatus(message);
    const searchValue = searchTerm.trim().toLowerCase();

    const clientName =
      message.fullName ||
      message.full_name ||
      "";

    const clientEmail =
      message.emailAddress ||
      message.email_address ||
      message.email ||
      "";

    const subject =
      message.documentType ||
      message.document_type ||
      "General message";

    const matchesStatus = messageStatus === statusFilter;

    const matchesSearch =
      !searchValue ||
      String(clientName).toLowerCase().includes(searchValue) ||
      String(clientEmail).toLowerCase().includes(searchValue) ||
      String(subject).toLowerCase().includes(searchValue);

    return matchesStatus && matchesSearch;
  });

  const exportVisibleMessages = async () => {
    if (!filteredMessages.length) {
      alert("No messages to export in this section.");
      return;
    }

    const exportedAt = new Date().toLocaleString("en-GB");

    const content = filteredMessages
      .map((message, index) => {
        const clientName =
          message.fullName ||
          message.full_name ||
          "Unknown client";

        const clientEmail =
          message.emailAddress ||
          message.email_address ||
          message.email ||
          "No email";

        const subject =
          message.subject ||
          message.documentType ||
          message.document_type ||
          "No subject";

        const status = message.status || "New";

        const submitted = formatDate(
          message.createdAt || message.created_at
        );

        const body =
          message.message ||
          message.notes ||
          "No message provided.";

        return `MESSAGE ${index + 1}
====================

Client: ${clientName}
Email: ${clientEmail}
Subject: ${subject}
Status: ${status}
Submitted: ${submitted}

Message:
${body}`;
      })
      .join("\n\n------------------------------\n\n");

    const finalContent = `YAICH TRANSLATIONS - MESSAGES EXPORT
====================================

Section: ${statusFilter}
Exported at: ${exportedAt}
Total messages: ${filteredMessages.length}

${content}`;
    const messageDates = filteredMessages
      .map((message) => new Date(message.createdAt || message.created_at))
      .filter((date) => !Number.isNaN(date.getTime()))
      .sort((firstDate, secondDate) => firstDate - secondDate);

    const formatFileDate = (date) => {
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();

      return `${day}-${month}-${year}`;
    };

    const oldestDate = messageDates.length
      ? formatFileDate(messageDates[0])
      : "unknown-date";

    const newestDate = messageDates.length
      ? formatFileDate(messageDates[messageDates.length - 1])
      : "unknown-date";

    const exportSection = statusFilter.toLowerCase();
    const blob = new Blob([finalContent], {
      type: "text/plain;charset=utf-8",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `messages-${exportSection}-${oldestDate}-${newestDate}.txt`;

    document.body.appendChild(link);
    link.click();
    link.remove();

    URL.revokeObjectURL(url);
    await onMarkExported(
      filteredMessages.map((message) => message.id)
    );
  };
  return (
    <div className="admin-panel">
      <div className="message-status-tabs">
        {messageTabs.map((tab) => (
          <button
            key={tab.value}
            type="button"
            className={
              statusFilter === tab.value
                ? "message-status-tab active"
                : "message-status-tab"
            }
            onClick={() => onStatusFilterChange(tab.value)}
          >
            <span>{tab.label}</span>
            <strong>{getMessageCount(tab.value)}</strong>
          </button>
        ))}
      </div>

      <div
        className="admin-panel-toolbar"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}
      >
        <input
          type="search"
          value={searchTerm}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search messages"
          className="admin-control-input"
          style={{
            flex: 1,
            minWidth: 0,
          }}
        />

        {(statusFilter === "Archived" || statusFilter === "Deleted") && (
          <button
            type="button"
            className="admin-email-action"
            onClick={exportVisibleMessages}
            disabled={!filteredMessages.length}
          >
            Export all {statusFilter} messages
          </button>
        )}
        {statusFilter === "Exported" && (
          <button
            type="button"
            onClick={() =>
              onBulkDelete(filteredMessages.map((message) => message.id))
            }
            disabled={
              !filteredMessages.length ||
              actionId === "messages-bulk-permanent-delete"
            }
            style={{
              height: "38px",
              minWidth: "230px",
              padding: "0 16px",
              flexShrink: 0,
              border: "1px solid #c81e1e",
              borderRadius: "9px",
              backgroundColor: "#c81e1e",
              color: "#ffffff",
              fontSize: "13px",
              fontWeight: "800",
              whiteSpace: "nowrap",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor:
                !filteredMessages.length ||
                  actionId === "messages-bulk-permanent-delete"
                  ? "not-allowed"
                  : "pointer",
              opacity:
                !filteredMessages.length ||
                  actionId === "messages-bulk-permanent-delete"
                  ? 0.6
                  : 1,
            }}
          >
            {actionId === "messages-bulk-permanent-delete"
              ? "Deleting..."
              : `Delete all exported messages (${filteredMessages.length})`}
          </button>
        )}
      </div>

      {filteredMessages.length === 0 ? (
        <p className="admin-empty-panel">No contact messages found.</p>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-data-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Subject</th>
                <th>Status</th>
                <th>Submitted</th>
                <th>Actions</th>
              </tr>
            </thead>

              <tbody>
                {filteredMessages.map((message) => (
                <tr key={message.id}>
                  <td>
                    <strong>{message.fullName}</strong>
                    <span>{message.emailAddress}</span>
                  </td>

                  <td>{message.documentType || "General message"}</td>

                  <td className="admin-status-cell">
                    <div className="admin-status-cell-inner">
                      <span
                        className={`message-status-badge message-status-${String(
                          message.status || "New"
                        )
                          .toLowerCase()
                          .replaceAll(" ", "_")}`}
                      >
                        {String(message.status || "New").replaceAll("_", " ")}
                      </span>
                    </div>
                  </td>

                  <td>{formatDate(message.createdAt)}</td>

                  <td>
                    <div className="admin-row-actions-compact">
                      <button type="button" onClick={() => onSelectMessage(message)}>
                        View
                      </button>

                        {getMessageStatus(message) === "Exported" ? (
                          <>
                            <button
                              type="button"
                              className="message-bulk-delete-btn"
                              onClick={() => onPermanentDelete(message.id)}
                              disabled={
                                actionId === `message-permanent-delete-${message.id}`
                              }
                            >
                              Delete permanently
                            </button>
                          </>
                        ) : getMessageStatus(message) === "Archived" ? (
                        <>
                          <button
                            type="button"
                            onClick={() => onRestore(message.id)}
                            disabled={actionId === `message-restore-${message.id}`}
                          >
                            Restore
                          </button>

                        </>
                        ) : getMessageStatus(message) === "Deleted" ? (
                          <>
                            <button
                              type="button"
                              onClick={() => onRestore(message.id)}
                              disabled={actionId === `message-restore-${message.id}`}
                            >
                              Restore
                            </button>


                            <button
                              type="button"
                              className="danger-action"
                              onClick={() => onPermanentDelete(message.id)}
                              disabled={actionId === `message-permanent-delete-${message.id}`}
                            >
                              Delete permanently
                            </button>
                          </>
                        ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => onArchive(message.id)}
                            disabled={actionId === `message-archive-${message.id}`}
                          >
                            Archive
                          </button>

                          <button
                            type="button"
                            className="danger-action"
                            onClick={() => onDelete(message.id)}
                            disabled={actionId === `delete-${message.id}`}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </td>                  
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function UsersTab({ users, formatDate }) {
  return (
    <div className="admin-panel">
      {users.length === 0 ? (
        <p className="admin-empty-panel">No users found.</p>
      ) : (
        <div className="admin-card-list">
          {users.map((user) => (
            <article key={user.id} className="admin-resource-card">
              <div>
                <h3>{user.fullName}</h3>
                <p>{user.email}</p>
              </div>

              <dl>
                <div>
                  <dt>Phone</dt>
                  <dd>{user.phone || "Not provided"}</dd>
                </div>

                <div>
                  <dt>Status</dt>
                  <dd>{user.status || "Active"}</dd>
                </div>

                <div>
                  <dt>Created</dt>
                  <dd>{formatDate(user.createdAt)}</dd>
                </div>
              </dl>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function OrdersTab({
  orders,
  orderStatusFilter,
  onOrderStatusFilterChange,
  ordersSearch,
  onOrdersSearchChange,
  formatDate,
  formatMoney,
  onDownloadZip,
  onDownloadFinalFile,
  onDeleteOrder,
  onRestoreDeletedOrder,
  onPermanentDeleteOrder,
  onStatusChange,
  onUploadFinalTranslation,
  onSendFinalTranslationEmail,
  onViewQuoteInfo,
  actionId,
}) {
  const normalizeStatus = (status) =>
    String(status || "in_progress")
      .toLowerCase()
      .replaceAll(" ", "_")
      .replaceAll("-", "_");

  const orderFilterTabs = [
    {
      value: "in_progress",
      label: "Converted Orders",
    },
    {
      value: "completed",
      label: "Ready to Send",
    },
    {
      value: "delivered",
      label: "Delivered",
    },
    {
      value: "cancelled",
      label: "Cancelled",
    },
    {
      value: "archived",
      label: "Archive",
    },
    {
      value: "recycle_bin",
      label: "Recycle Bin",
    },
  ];

  const isDeletedOrder = (order) => Boolean(order.deleted_at || order.deletedAt);

  const getCountByStatus = (statusValue) => {
    if (statusValue === "recycle_bin") {
      return orders.filter((order) => isDeletedOrder(order)).length;
    }

    return orders.filter(
      (order) =>
        !isDeletedOrder(order) &&
        normalizeStatus(order.status) === statusValue
    ).length;
  };

  const normalizedOrdersSearch = (ordersSearch || "").trim().toLowerCase();

  const filteredOrders = orders.filter((order) => {
    const orderCode =
      order.orderNumber ||
      order.order_number ||
      order.trackingCode ||
      order.tracking_code ||
      "";

    const clientName =
      order.clientName ||
      order.client_name ||
      order.fullName ||
      order.full_name ||
      order.userFullName ||
      order.user_full_name ||
      "";

    const clientEmail =
      order.email ||
      order.clientEmail ||
      order.client_email ||
      order.userEmail ||
      order.user_email ||
      "";

    const orderStatus = normalizeStatus(order.status);


    const deletedOrder = isDeletedOrder(order);

    const matchesTab =
      orderStatusFilter === "recycle_bin"
        ? deletedOrder
        : !deletedOrder && orderStatus === orderStatusFilter;

    const matchesSearch =
      !normalizedOrdersSearch ||
      String(orderCode).toLowerCase().includes(normalizedOrdersSearch) ||
      String(clientName).toLowerCase().includes(normalizedOrdersSearch) ||
      String(clientEmail).toLowerCase().includes(normalizedOrdersSearch);

    return matchesTab && matchesSearch;
  });
  return (
    <div className="admin-panel orders-panel">
      <div className="admin-orders-searchbar-wrap">
        <input
          type="text"
          className="admin-orders-searchbar"
          placeholder="Search order code, client name, or email"
          value={ordersSearch}
          onChange={(event) => onOrdersSearchChange(event.target.value)}
        />
      </div>

      <div className="admin-order-filter-tabs-wrap">
        <div className="admin-order-filter-tabs">
          {orderFilterTabs.map((tab) => (
            <button
              key={tab.value}
              type="button"
              className={
                orderStatusFilter === tab.value
                  ? "admin-order-filter-tab active"
                  : "admin-order-filter-tab"
              }
              onClick={() => onOrderStatusFilterChange(tab.value)}
            >
              <span>{tab.label}</span>
              <strong>{getCountByStatus(tab.value)}</strong>
            </button>
          ))}
        </div>
      </div>

      {filteredOrders.length === 0 ? (
        <p className="admin-empty-panel">No orders found in this section.</p>
      ) : (
        <div className="admin-orders-list">
          {filteredOrders.map((order) => {
            const orderNumber =
              order.orderNumber ||
              order.order_number ||
              order.trackingCode ||
              order.tracking_code ||
              `ORDER-${order.id}`;

            const orderTitle =
              order.title ||
              order.documentType ||
              order.document_type ||
              "Translation order";

            const statusValue = normalizeStatus(order.status);

            const sourceFiles = (order.files || []).filter(
              (file) => (file.fileRole || file.file_role || "Source") !== "Final"
            );

            const finalFiles = (order.files || []).filter(
              (file) => (file.fileRole || file.file_role) === "Final"
            );

            const firstFinalFile = finalFiles[0];

            const firstFinalFileName =
              firstFinalFile?.originalName ||
              firstFinalFile?.original_name ||
              firstFinalFile?.filename ||
              "Download Translation";

            const uploadInputId = `final-upload-${order.id}`;

            const openUploadDialog = () => {
              document.getElementById(uploadInputId)?.click();
            };

            const downloadFinalTranslation = () => {
              if (firstFinalFile?.id) {
                onDownloadFinalFile(firstFinalFile.id, firstFinalFileName);
                return;
              }

              onViewQuoteInfo(order);
            };

            const allowFinalFilesDrop = (event) => {
              event.preventDefault();
              event.stopPropagation();

              event.currentTarget.classList.add("drag-over");
            };

            const removeFinalFilesDropEffect = (event) => {
              event.preventDefault();
              event.stopPropagation();

              event.currentTarget.classList.remove("drag-over");
            };
            
            const deletedOrder = isDeletedOrder(order);

            if (deletedOrder && orderStatusFilter === "recycle_bin") {
              return (
                <article key={order.id} className="admin-order-card-six-grid">
                  <button
                    type="button"
                    className="order-six-cell order-code-cell"
                    onClick={() => onViewQuoteInfo(order)}
                  >
                    {orderNumber}
                  </button>

                  <button
                    type="button"
                    className="order-six-cell order-normal-cell"
                    onClick={() => onDownloadZip(order)}
                    disabled={!sourceFiles.length}
                  >
                    Download ZIP
                  </button>

                  <button
                    type="button"
                    className="order-six-cell order-danger-cell"
                    onClick={() => onPermanentDeleteOrder(order.id)}
                    disabled={actionId === `order-permanent-delete-${order.id}`}
                  >
                    Delete Permanently
                  </button>

                  <button
                    type="button"
                    className="order-six-cell order-normal-cell"
                    onClick={() => onViewQuoteInfo(order)}
                  >
                    View Details
                  </button>

                  <button
                    type="button"
                    className="order-six-cell order-restore-cell"
                    onClick={() => onRestoreDeletedOrder(order.id)}
                    disabled={actionId === `order-restore-deleted-${order.id}`}
                  >
                    Restore Order
                  </button>

                  <button
                    type="button"
                    className="order-six-cell order-disabled-cell"
                    disabled
                  >
                    Deleted
                  </button>
                </article>
              );
            }
            const handleFinalFilesDrop = (event) => {
              event.preventDefault();
              event.stopPropagation();

              event.currentTarget.classList.remove("drag-over");

              if (actionId === `order-upload-final-${order.id}`) return;

              const droppedFiles = event.dataTransfer.files;

              if (droppedFiles && droppedFiles.length > 0) {
                onUploadFinalTranslation(order.id, droppedFiles);
              }
            };
            return (
              <article key={order.id} className="admin-order-card-six-grid">
                <input
                  id={uploadInputId}
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  style={{ display: "none" }}
                  onChange={(event) => {
                    onUploadFinalTranslation(order.id, event.target.files);
                    event.target.value = "";
                  }}
                />
                <button
                  type="button"
                  className="order-six-cell order-code-cell"
                  onClick={() => onViewQuoteInfo(order)}
                  title="View order details"
                >
                  {orderNumber}
                </button>

                {statusValue === "completed" ? (
                  <button
                    type="button"
                    className="order-six-cell order-primary-cell order-drop-zone"
                    onClick={openUploadDialog}
                    onDragOver={allowFinalFilesDrop}
                    onDragEnter={allowFinalFilesDrop}
                    onDrop={handleFinalFilesDrop}
                    onDragLeave={removeFinalFilesDropEffect}
                    disabled={actionId === `order-upload-final-${order.id}`}
                  >
                    Replace
                  </button>
                ) : statusValue === "delivered" ? (
                  <button
                    type="button"
                    className="order-six-cell order-normal-cell"
                    onClick={downloadFinalTranslation}
                  >
                    Download Translation
                  </button>
                ) : (
                  <button
                    type="button"
                    className="order-six-cell order-normal-cell"
                    onClick={() => onDownloadZip(order)}
                    disabled={!sourceFiles.length}
                  >
                    Download ZIP
                  </button>
                )}

                <button
                  type="button"
                  className="order-six-cell order-danger-cell"
                  onClick={() => onDeleteOrder(order.id)}
                  disabled={actionId === `order-delete-${order.id}`}
                >
                  Delete Order
                </button>

                <button
                  type="button"
                  className="order-six-cell order-normal-cell"
                  onClick={() => onViewQuoteInfo(order)}
                >
                  View Details
                </button>

                {statusValue === "completed" ? (
                  <button
                    type="button"
                    className="order-six-cell order-primary-cell"
                    onClick={() => onSendFinalTranslationEmail(order.id)}
                    disabled={actionId === `order-send-final-${order.id}`}
                  >
                    Send via Email
                  </button>
                ) : statusValue === "delivered" ? (
                    <button
                      type="button"
                      className="order-six-cell order-normal-cell order-drop-zone"
                      onClick={openUploadDialog}
                      onDragOver={allowFinalFilesDrop}
                      onDragEnter={allowFinalFilesDrop}
                      onDrop={handleFinalFilesDrop}
                      disabled={actionId === `order-upload-final-${order.id}`}
                    >
                      Replace Translation
                    </button>
                  ) : statusValue === "cancelled" ? (
                    <button
                      type="button"
                      className="order-six-cell order-restore-cell"
                      onClick={async () => {
                        const confirmed = window.confirm("Recover this order?");

                        if (!confirmed) return;

                        await onStatusChange(order.id, "in_progress");
                      }}
                      disabled={actionId === `order-status-${order.id}`}
                    >
                      Recover Order
                    </button>
                  ) : statusValue === "archived" ? (
                    <button
                      type="button"
                      className="order-six-cell order-restore-cell"
                      onClick={async () => {
                        const confirmed = window.confirm("Restore this archived order?");

                        if (!confirmed) return;

                        await onStatusChange(order.id, "in_progress");
                      }}
                      disabled={actionId === `order-status-${order.id}`}
                    >
                      Restore Order
                    </button>
                  ) : (
                          <button
                            type="button"
                            className="order-six-cell order-primary-cell order-drop-zone"
                            onClick={openUploadDialog}
                            onDragOver={allowFinalFilesDrop}
                            onDragEnter={allowFinalFilesDrop}
                            onDrop={handleFinalFilesDrop}
                            disabled={actionId === `order-upload-final-${order.id}`}
                          >
                            Upload Translation
                          </button>
                )}
                {statusValue === "delivered" ? (
                  <button
                    type="button"
                    className="order-six-cell order-archive-cell"
                    onClick={async () => {
                      const confirmed = window.confirm("Archive this delivered order?");

                      if (!confirmed) return;

                      await onStatusChange(order.id, "archived");
                    }}
                    disabled={actionId === `order-status-${order.id}`}
                  >
                    Archive Order
                  </button>
                ) : statusValue === "cancelled" ? (
                  <button
                    type="button"
                    className="order-six-cell order-archive-cell"
                    onClick={async () => {
                      const confirmed = window.confirm("Archive this cancelled order?");

                      if (!confirmed) return;

                      await onStatusChange(order.id, "archived");
                    }}
                    disabled={actionId === `order-status-${order.id}`}
                  >
                    Archive
                  </button>
                ) : statusValue === "archived" ? (
                  <button
                    type="button"
                    className="order-six-cell order-disabled-cell"
                    disabled
                  >
                    Archived
                  </button>
                ) : (
                  <button
                    type="button"
                    className="order-six-cell order-danger-cell"
                    onClick={async () => {
                      const confirmed = window.confirm("Cancel this order?");

                      if (!confirmed) return;

                      await onStatusChange(order.id, "cancelled");
                    }}
                    disabled={actionId === `order-status-${order.id}`}
                  >
                    Cancel Order
                  </button>
                )}              
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

function QuoteModal({
  quote,
  actionId,
  onClose,
  onStatusChange,
  onPriceChange,
  onDeleteQuote,
  onConvertQuote,
  onDownloadQuoteFile,
  onSendPaymentLink,
  formatDate,
  formatMoney,
}) {
  const [priceDraft, setPriceDraft] = useState(quote.estimated_price ?? "");
  const trackingCode = getQuoteTrackingCode(quote);

  const sourceFiles = (quote.files || []).filter(
    (file) => (file.fileRole || file.file_role || "Source") !== "Names"
  );

  const namesFiles = (quote.files || []).filter(
    (file) => (file.fileRole || file.file_role) === "Names"
  );

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={(event) => event.stopPropagation()}>
        <div className="admin-modal-header">
          <div>
            <h3>{trackingCode}</h3>
            <p>{quote.full_name || quote.fullName || quote.name || "Unknown client"}</p>
          </div>

          <button type="button" className="admin-modal-close" onClick={onClose}>
            x
          </button>
        </div>

        <div className="admin-modal-body">
          <div className="admin-detail-grid">
            <div>
              <span className="admin-label">Tracking Code</span>
              <p>{trackingCode}</p>
            </div>

            <div>
              <span className="admin-label">Client Name</span>
              <p>{quote.full_name || quote.fullName || quote.name || "Not provided"}</p>
            </div>

            <div>
              <span className="admin-label">Email</span>
              <p>{quote.email || quote.email_address || quote.emailAddress || "Not provided"}</p>
            </div>

            <div>
              <span className="admin-label">Submitted</span>
              <p>{formatDate(quote.created_at)}</p>
            </div>

            <div>
              <span className="admin-label">Source Language</span>
              <p>{quote.source_language || "Not specified"}</p>
            </div>

            <div>
              <span className="admin-label">Target Language</span>
              <p>{quote.target_language || "Not specified"}</p>
            </div>

            <div>
              <span className="admin-label">Translation Type</span>
              <p>{quote.translation_type || "Not specified"}</p>
            </div>

            <div>
              <span className="admin-label">Deadline</span>
              <p>{quote.deadline || "Not specified"}</p>
            </div>

            <div>
              <span className="admin-label">Current Price</span>
              <p>{formatMoney(quote.estimated_price, "GBP")}</p>
            </div>

            <div>
              <span className="admin-label">Status</span>
              <span
                className={`quote-status-badge quote-status-${String(
                  quote.status || "new"
                )
                  .toLowerCase()
                  .replaceAll(" ", "_")}`}
              >
                {String(quote.status || "new").replaceAll("_", " ")}
              </span>
            </div>

            <div>
              <span className="admin-label">Set Price GBP</span>
              <div className="admin-row-actions-compact">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={priceDraft}
                  onChange={(event) => setPriceDraft(event.target.value)}
                  className="admin-control-input"
                  style={{ maxWidth: "130px" }}
                />

                <button
                  type="button"
                  onClick={() => onPriceChange(quote.id, priceDraft)}
                  disabled={actionId === `quote-price-${quote.id}`}
                >
                  Save
                </button>
              </div>
            </div>
          </div>

          <div className="admin-modal-row">
            <span className="admin-label">Message</span>
            <p className="admin-message-text">
              {quote.message || "No message provided."}
            </p>
          </div>

          <div className="admin-modal-row">
            <span className="admin-label">Documents to translate</span>

            {sourceFiles.length ? (
              <div className="admin-files-list">
                {sourceFiles.map((file, index) => (
                  <div
                    key={`${file.id || file.originalName || file.original_name || "source-file"}-${index}`}
                    className="admin-file-row"
                  >
                    <span>
                      {file.originalName ||
                        file.original_name ||
                        file.filename ||
                        "Document to translate"}
                    </span>

                    <button
                      type="button"
                      className="admin-download-btn"
                      onClick={() => onDownloadQuoteFile(quote, file, index)}
                    >
                      Download
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p>No translation documents linked to this quote yet.</p>
            )}
          </div>

          {namesFiles.length > 0 && (
            <div className="admin-modal-row">
              <span className="admin-label">Names attachment</span>

              <div className="admin-files-list">
                {namesFiles.map((file, index) => (
                  <div
                    key={`${file.id || file.originalName || file.original_name || "names-file"}-${index}`}
                    className="admin-file-row names-file-row"
                  >
                    <span>
                      {file.originalName ||
                        file.original_name ||
                        file.filename ||
                        "Names attachment"}
                    </span>

                    <button
                      type="button"
                      className="admin-download-btn"
                      onClick={() => onDownloadQuoteFile(quote, file, index)}
                    >
                      Download
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="admin-modal-actions">
            <button
              type="button"
              className="admin-email-action"
              onClick={() => onSendPaymentLink(quote.id)}
              disabled={actionId === `quote-payment-${quote.id}`}
            >
              Send Quote & Payment Link
            </button>

            <button
              type="button"
              className="admin-email-action"
              onClick={() => onConvertQuote(quote.id)}
              disabled={actionId === `quote-convert-${quote.id}`}
            >
              Convert to Order
            </button>

            <button
              type="button"
              className="admin-delete-btn"
              onClick={() => onDeleteQuote(quote.id)}
              disabled={actionId === `quote-delete-${quote.id}`}
            >
              Delete Quote
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MessageModal({
  message,
  actionId,
  onClose,
  onStatusChange,
  onDelete,
  onDownload,
  onReply,
  formatDate,
}) {
  const [replyDraft, setReplyDraft] = useState("");

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={(event) => event.stopPropagation()}>
        <div className="admin-modal-header">
          <div>
            <h3>{message.fullName}</h3>
            <p>{message.emailAddress}</p>
          </div>

          <button type="button" className="admin-modal-close" onClick={onClose}>
            x
          </button>
        </div>

        <div className="admin-modal-body">
          <div className="admin-detail-grid">
            <div>
              <span className="admin-label">Subject</span>
              <p>{message.documentType || "General message"}</p>
            </div>

            <div>
              <span className="admin-label">Submitted</span>
              <p>{formatDate(message.createdAt)}</p>
            </div>

            <div>
              <span className="admin-label">Status</span>
              <span
                className={`message-status-badge message-status-${String(
                  message.status || "New"
                )
                  .toLowerCase()
                  .replaceAll(" ", "_")}`}
              >
                {String(message.status || "New").replaceAll("_", " ")}
              </span>
            </div>
          </div>

          <div className="admin-modal-row">
            <span className="admin-label">Client Message</span>
            <p className="admin-message-text">{message.message}</p>
          </div>

          <div className="admin-modal-row">
            <span className="admin-label">Reply Message</span>


            <textarea
              className="admin-control-input"
              rows="6"
              value={replyDraft}
              onChange={(event) => setReplyDraft(event.target.value)}
              placeholder="Type your reply here. It will be inserted into the email template."
              style={{ width: "100%", resize: "vertical" }}
            />
          </div>

          <div className="admin-modal-actions">
            <button
              type="button"
              className="admin-email-action"
              onClick={() => onReply(message.id, replyDraft)}
              disabled={actionId === `reply-${message.id}` || !replyDraft.trim()}
            >
              Send Reply
            </button>

            <button
              type="button"
              className="admin-delete-btn"
              onClick={() => onDelete(message.id)}
              disabled={actionId === `delete-${message.id}`}
            >
              Delete Message
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

